import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AlertController } from "ionic-angular";
import { flatten, isArray, isUndefined, map, toString, values } from "lodash";
import { Observable } from "rxjs/Observable";
import { ConfigurationService } from "../configuration/configuration.service";
import { MasterDataManager } from "../offline-manager/master-data-manager";
import { stringifyJSON } from "../offline-manager/offline-globals";
import {
  AssessmentSectionTemplateConfig,
  AssessmentTemplateConfig,
  AssessmentType,
  Lookup,
  TeamRole,
  TemplateRules,
  UserRole,
} from "./master-data-interface";

@Injectable()
export class MasterDataService {
  assessmentUrl: string;

  constructor(
    public http: HttpClient,
    public masterDataManager: MasterDataManager,
    private alertCtrl: AlertController,
    private config: ConfigurationService
  ) {
    this.assessmentUrl = config.getConfig().apiUrl;
  }

  fetchAssesmentMasterData(): Observable<any> {
    return this.http.get(`${this.assessmentUrl}/assessmentOffline/masterData/`);
  }

  loadMasterData() {
    const isMasterDataLoaded = localStorage.getItem("isMasterDataLoaded");
    if (isMasterDataLoaded === null || isMasterDataLoaded === "false") {
      this.fetchAssesmentMasterData().subscribe((data) => {
        if (isUndefined(data)) {
          return;
        }
        this.masterDataManager
          .clearMasterData(data.userDetails.userId)
          .subscribe(
            () => {
              this.createMasterData(data);
              localStorage.setItem("isMasterDataLoaded", "true");
            },
            (error) => {
              let alert = this.alertCtrl.create({
                title: "SQLite master data download",
                message: stringifyJSON(error),
                buttons: ["Dismiss"],
              });
              alert.present();
            }
          );
      });
    }
  }

  createMasterData(data: any) {
    this.createUser(data.userDetails);
    this.createRole(data.roles);
    this.createLookup(data.lookupByCodeMap);
    this.createAssesmentType(data.assessmentTypes);
    this.createRulesTemplate(data.rulesTemplate);
    this.createAsmtTemplateConfig(data.assessmentTemplateConfig);
    this.createAsmtSectionTemplateConfig(data.assessmentSectionTemplateConfig);
    this.createPermissionDictionary(data.dictionary);
  }

  createUser(userDetails) {
    if (isUndefined(userDetails)) {
      return;
    }
    const user: UserRole = {
      userId: Number(userDetails.userId),
      userRole:
        userDetails.currentRoles.length > 0
          ? userDetails.currentRoles[0].name
          : "",
      tenantId: userDetails.tenantId,
      qbosUserId: userDetails.qbosUserId,
    };
    this.masterDataManager.createUserRole(user);
  }

  createRole(roles) {
    if (isUndefined(roles)) {
      return;
    }
    const teamRoles = [];
    map(roles, (data) => {
      const { tenantId, teamRole, roleLookupCode, workGroup } = data;
      let role: TeamRole = {
        tenantId: tenantId || 0,
        teamRole,
        roleLookupCode,
        workGroup,
      };
      teamRoles.push(role);
    });
    this.masterDataManager.createTeamRole(teamRoles);
  }

  createLookup(lookupByCodeMap) {
    if (isUndefined(lookupByCodeMap)) {
      return;
    }
    const lookupCodes = [];
    map(values(lookupByCodeMap), (data) => {
      const {
        tenantId,
        lookupCode,
        lookupTypeCode,
        lookupValue,
        lookupValue1,
        description,
        sortSeq,
        isDefaultVisible,
        lookupRefKey,
      } = data;
      let lookup: Lookup = {
        tenantId: tenantId || 0,
        lookupCode,
        lookupTypeCode,
        lookupValue,
        lookupValue1,
        description,
        sortSequence: sortSeq,
        isDefaultVisible,
        lookupReferenceKey: lookupRefKey,
      };
      lookupCodes.push(lookup);
    });
    this.masterDataManager.createLookup(lookupCodes);
  }

  createAssesmentType(asmtType) {
    if (isUndefined(asmtType)) {
      return;
    }
    const asmtTypes = [];
    map(values(asmtType), (data) => {
      const {
        tenantId,
        asmtTypeLookupCode,
        levelOfCareLookupCode,
        roleLookupCode,
      } = data;
      let assessmentTypes: AssessmentType = {
        tenantId: tenantId || 0,
        assessmentTypeLookupCode: asmtTypeLookupCode,
        levelOfCareLookupCode,
        roleLookupCode,
      };
      asmtTypes.push(assessmentTypes);
    });
    this.masterDataManager.createAssessmentType(asmtTypes);
  }

  createRulesTemplate(rulesTemplate) {
    if (isUndefined(rulesTemplate)) {
      return;
    }
    const templateRules = [];
    map(rulesTemplate, (data) => {
      const {
        tenantId,
        tmplPath,
        tmplJson,
        asmtTypeLookupCode,
        roleLookupCode,
      } = data;
      let templateRule: TemplateRules = {
        tenantId: tenantId || 0,
        tmplPath,
        tmplJson: stringifyJSON(tmplJson),
        asmtTypeLookupCode: toString(asmtTypeLookupCode),
        roleLookupCode: toString(roleLookupCode),
      };
      templateRules.push(templateRule);
    });
    this.masterDataManager.createRulesTemplate(templateRules);
  }

  createAsmtTemplateConfig(assessmentTemplateConfig) {
    if (isUndefined(assessmentTemplateConfig)) {
      return;
    }
    const asmtTmplConfig = [];
    map(assessmentTemplateConfig, (data) => {
      const { tenantId, tmplPath, tmplVersion, tmplJson, asmtTypeLookupCode } =
        data;
      let templConfig: AssessmentTemplateConfig = {
        tenantId: tenantId || 0,
        tmplVersion,
        tmplPath,
        tmplJson: stringifyJSON(tmplJson),
        asmtTypeLookupCode,
      };
      asmtTmplConfig.push(templConfig);
    });
    this.masterDataManager.createAsmtTemplateConfig(asmtTmplConfig);
  }

  createAsmtSectionTemplateConfig(assessmentSectionTemplateConfig) {
    if (isUndefined(assessmentSectionTemplateConfig)) {
      return;
    }
    const asmtSectionTmplConfig = [];
    map(assessmentSectionTemplateConfig, (data) => {
      const {
        tenantId,
        tmplPath,
        tmplVersion,
        tmplJson,
        asmtTypeLookupCode,
        sectionTypeLookupCode,
      } = data;
      let sectionTemplConfig: AssessmentSectionTemplateConfig = {
        tenantId: tenantId || 0,
        tmplVersion,
        tmplPath,
        tmplJson: stringifyJSON(tmplJson),
        asmtTypeLookupCode,
        sectionTypeLookupCode,
      };
      asmtSectionTmplConfig.push(sectionTemplConfig);
    });
    this.masterDataManager.createAsmtSectionTemplateConfig(
      asmtSectionTmplConfig
    );
  }

  createPermissionDictionary(dictionary) {
    if (!isArray(dictionary)) {
      return;
    }
    const permDictionary = [];
    map(dictionary, (dict) => {
      const accessMap = this.preparePermissionDictionaryAccess(dict);
      permDictionary.push(accessMap);
    });
    this.masterDataManager.createPermissionDictionary(flatten(permDictionary));
  }

  preparePermissionDictionaryAccess(dict) {
    const access = [];
    map(dict.accessTbl, (val, code) => {
      let dictMap = {
        groupId: dict.groupId,
        workgroup: dict.workgroup,
        accessLookupCode: code,
        accessLookupValue: val,
      };
      access.push(dictMap);
    });
    return access;
  }
}



// WEBPACK FOOTER //
// ./src/providers/master-data-service/master-data-service.ts